import _ from 'lodash';
import autobind from 'autobind-decorator';

import {
	observable,
	action,
	computed,
	observe,
	reaction,
	extendObservable,
	runInAction,
	toJS
} from 'mobx';

import BaseStore from './base-store';
import apiStore from './api-store';
import formStore from './form-store';
import config from '../config';

const { routes } = config;

class UserStore extends BaseStore {
	apiRoute = routes.users;
	fetchPagedItemsRoute = routes.usersPaged;

	roles = {
		ADMIN: 'ADMIN',
		ARTIST: 'ARTIST',
		SUBSCRIBER: 'SUBSCRIBER'
	};

	@observable
	loginError = false;

	@observable
	_newProfileImageData = '';

	constructor () {
		super();

		reaction(() => apiStore.data.user, this.onNewData);
		observe(this, '_newProfileImageData', this.saveProfileImage);
	}

	@computed
	get data () {
		return {
			user: this._data.item
		};
	}

	@computed
	get users () {
		return this._items;
	}

	@computed
	get userNameForFilePath () {
		const { _id = '' } = this.data.user.toPOJO();
		return _id;
	}

	@computed
	get subscriberList () {
		return _(this.users)
			.filter(({ role }) => role === this.roles.SUBSCRIBER)
			.sortBy('createdAt')
			.value();
	}

	@computed
	get artistList () {
		return this.users
			.filter(({ role }) => role === this.roles.ARTIST);
	}

	@autobind
	@action
	onNewData (change) {
		this.data.user.merge(toJS(change));
		this.isLoading = false;
	}

	@action
	setProfileImageData (imageData) {
		this._newProfileImageData = imageData;
	}

	@action
	async saveUser (payload = null) {
		const _id = this.get('user._id') || null;

		if (_.isString(_id) && _.isEmpty(payload)) {

			payload = this.data.user.toPOJO();
			payload.type = this.changeTypes.UPDATE;
			this.silentDataUpdate = false;
		}

		const data = await this.updateItem(payload);
		this.data.user.merge(data);
		return data;
	}

	@action
	async createUser (user) {
		const { data } = await this.saveData(user, routes.createUser);
		this.data.user.merge(data);
		return data;
	}

	@autobind
	@action
    async saveProfileImage({ newValue: imageData }) {
        const _id = this.get('user._id');
        const route = routes.profileImageUpload.replace(/\:id/, _id);
		const payload = {
			imageData,
			path: `profile-images/${_id}`
		};

		try {

			const { data: { path } } = await this.ax.post(route, payload);
			runInAction('Update with saved profile image', () => {
				this.set({
					key: 'user.profileImage',
					value: path,
					silent: true
				});
			});
		} catch (err) {

			console.error('[ERROR: UserStore.saveProfileImage]', err);
		}
	}

	@action
	async removeProfileImage ({ _id }) {
		const route = routes.removeProfileImage.replace(/\:_id/, _id);

		try {

			await this.ax.delete(route);
			runInAction('Update with saved profile image', () => {
				const index = this.getItemIndex({ _id });
				this._items[index].profileImage = '';
			});
		} catch (err) {

			console.error('[ERROR: UserStore.removeProfileImage]', err);
		}
	}

	@action
	async checkLogin (opts = { sendRedirect: true }) {
		this.isLoading = true;

		try {

			const user = this.data.user.toPOJO();
			const { userName, password } = _.isEmpty(user) ? opts : user;

			if (_.isEmpty(userName) || _.isEmpty(password)) {

				throw new Error('No login credentials found.');
			}

			const response = await this.ax.post(routes.login, {
				userName, password
			});

			runInAction('Check user login', () => {
				const { routeTo, _id: uid } = response.data;
 				const sessionObj = { routeTo, uid };

 				window.sessionStorage.setItem('hfm', JSON.stringify(sessionObj));
				this.loginError = false;
			});

			if (opts.sendRedirect) {

				window.location.href = response.data.routeTo;
			}
		} catch (err) {

			console.error('[ERROR: UserStore.checkLogin]', err);
			this.loginError = true;
		} finally {

			this.isLoading = false;
		}
	}

	@action
	setCurrent (_id) {
		if (_.isEmpty(_id)) {

			return;
		}

		this.data.user.replace(this.getItemByID({ _id }));
	}

	checkEmail (email) {
		try {

			return this.ax.get(`/api/users/check-email/${email}`);
		} catch (err) {

			console.error('[ERROR: BaseStore.checkEmail]', err);
		}
	}
}

const userStore = new UserStore();

export default userStore;
